'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { useUrlInspect, UrlInspectResult } from '@/hooks/useCatalog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Search, AlertCircle, CheckCircle2, AlertTriangle,
  XCircle, Copy, Check, ChevronRight, ExternalLink,
} from 'lucide-react';
import { catalogColors } from '@/components/catalog/catalogColors';

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_META = {
  ok:      { icon: CheckCircle2,  color: catalogColors.accentTeal, label: 'OK' },
  warn:    { icon: AlertTriangle, color: catalogColors.amber,      label: 'WARN' },
  error:   { icon: XCircle,       color: '#EF4444',                label: 'ERROR' },
  missing: { icon: AlertCircle,   color: catalogColors.gray,       label: 'MISSING' },
} as const;

function SignalIcon({ status, size = 16 }: { status: string; size?: number }) {
  const meta = STATUS_META[status as keyof typeof STATUS_META] ?? STATUS_META.missing;
  const Icon = meta.icon;
  return <Icon style={{ color: meta.color }} size={size} />;
}

function StatusChip({ status }: { status: string }) {
  const meta = STATUS_META[status as keyof typeof STATUS_META] ?? STATUS_META.missing;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: meta.color + '20', color: meta.color }}
    >
      {status}
    </span>
  );
}

// ─── Match strategy banner ────────────────────────────────────────────────────

const STRATEGY_META: Record<string, { label: string; color: string }> = {
  exact_normalized:  { label: 'Exact URL match',              color: catalogColors.accentTeal },
  path_only:         { label: 'Path match (params stripped)', color: catalogColors.amber },
  slug:              { label: 'Slug match',                   color: catalogColors.amber },
  ambiguous_resolved:{ label: 'Ambiguous — auto-resolved to freshest variant', color: catalogColors.amber },
  not_found:         { label: 'Not found',                    color: '#EF4444' },
};

const STRATEGY_CONFIDENCE: Record<string, number> = {
  exact_normalized:   1.0,
  path_only:          0.85,
  slug:               0.65,
  ambiguous_resolved: 0.7,
  not_found:          0,
};

function MatchStrategyBanner({ data }: { data: UrlInspectResult }) {
  // match_strategy may be 'ambiguous_resolved' at runtime even though the TS union
  // doesn't include it yet — cast to string for the lookup.
  const strategyKey = data.match_strategy as string;
  const meta = STRATEGY_META[strategyKey] ?? STRATEGY_META.not_found;
  // Use server-supplied confidence (0–1) when available; fall back to strategy defaults.
  const confidenceVal = data.confidence ?? STRATEGY_CONFIDENCE[strategyKey] ?? 0;
  const pct = Math.round(confidenceVal * 100);
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-lg border text-sm"
      style={{ borderColor: meta.color + '60', backgroundColor: meta.color + '0C' }}
    >
      <SignalIcon status={pct >= 80 ? 'ok' : pct > 0 ? 'warn' : 'error'} size={18} />
      <div className="flex-1 min-w-0">
        <span className="font-semibold" style={{ color: meta.color }}>{meta.label}</span>
        {data.normalized_url && (
          <span className="ml-2 text-xs text-muted-foreground font-mono truncate">
            {data.normalized_url}
          </span>
        )}
      </div>
      <div className="shrink-0 text-right">
        <div className="text-xs font-semibold" style={{ color: meta.color }}>{pct}% confidence</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{data.match_strategy}</div>
      </div>
    </div>
  );
}

// ─── Overall health banner ────────────────────────────────────────────────────

const HEALTH_META = {
  healthy:  { color: catalogColors.accentTeal, label: 'All signals healthy', icon: CheckCircle2 },
  has_gaps: { color: catalogColors.amber,      label: 'Some signals need attention', icon: AlertTriangle },
  broken:   { color: '#EF4444',                label: 'Critical issues detected', icon: XCircle },
} as const;

function HealthBanner({ status }: { status: 'healthy' | 'has_gaps' | 'broken' }) {
  const meta = HEALTH_META[status];
  const Icon = meta.icon;
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium"
      style={{ backgroundColor: meta.color + '15', color: meta.color }}
    >
      <Icon size={16} />
      {meta.label}
    </div>
  );
}

// ─── Signal card (collapsible) ────────────────────────────────────────────────

function asStr(v: unknown): string | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

function DetailGrid({ detail }: { detail: Record<string, unknown> }) {
  const rows = Object.entries(detail)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .flatMap(([k, v]): Array<[string, string]> => {
      if (Array.isArray(v)) return [[k, `[${v.length} items]`]];
      if (v !== null && typeof v === 'object') return [[k, JSON.stringify(v).slice(0, 120)]];
      return [[k, String(v)]];
    });
  if (rows.length === 0) return <div className="text-xs text-muted-foreground">No details available.</div>;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
      {rows.map(([k, v]) => (
        <div key={k} className="flex gap-2 min-w-0">
          <span className="text-muted-foreground shrink-0 font-mono">{k}:</span>
          <span className="text-foreground truncate">{v}</span>
        </div>
      ))}
    </div>
  );
}

function SignalCard({ signal }: { signal: UrlInspectResult['signals'][number] }) {
  const meta = STATUS_META[signal.status] ?? STATUS_META.missing;
  const detail = signal.detail as Record<string, unknown>;

  return (
    <AccordionItem
      value={String(signal.stage)}
      className="border rounded-lg px-3 data-[state=open]:shadow-sm"
      style={{ borderColor: meta.color + '40' }}
    >
      <AccordionTrigger className="py-3 hover:no-underline">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <SignalIcon status={signal.status} size={15} />
          <span className="text-xs font-semibold text-muted-foreground w-5 shrink-0">#{signal.stage}</span>
          <span className="text-sm font-medium truncate">{signal.label}</span>
          <StatusChip status={signal.status} />
          <span className="text-xs text-muted-foreground ml-auto mr-2 truncate hidden sm:block">{signal.summary}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-3">
        <div className="pl-8 space-y-3">
          {signal.summary && (
            <p className="text-sm text-muted-foreground">{signal.summary}</p>
          )}
          {detail && Object.keys(detail).length > 0 && <DetailGrid detail={detail} />}
          {signal.drill_url && (
            <a
              href={signal.drill_url}
              className="inline-flex items-center gap-1 text-xs hover:underline"
              style={{ color: catalogColors.accentTeal }}
            >
              {signal.drill_label ?? 'View details'} <ExternalLink size={10} />
            </a>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

// ─── Cross-site drill-down ────────────────────────────────────────────────────

function CrossSitePanel({ occurrences }: { occurrences: UrlInspectResult['product_card']['cross_site_occurrences'] }) {
  const navigate = useNavigate();
  if (!occurrences || occurrences.length === 0) return null;
  return (
    <div className="rounded-lg border p-4 space-y-2">
      <div className="text-sm font-semibold text-foreground">
        Same product on {occurrences.length} other site{occurrences.length !== 1 ? 's' : ''}
      </div>
      <div className="divide-y divide-border">
        {occurrences.map((occ) => (
          <div key={occ.site_id} className="flex items-center justify-between py-2 gap-3">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{occ.site_name}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">{occ.url}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {occ.is_live_on_site && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                  style={{ backgroundColor: catalogColors.accentTeal + '20', color: catalogColors.accentTeal }}
                >
                  LIVE
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => navigate(
                  `/traceability?q=${encodeURIComponent(occ.url)}&site_id=${encodeURIComponent(occ.site_id)}`
                )}
              >
                Inspect <ChevronRight size={12} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Copy-to-clipboard artifact ───────────────────────────────────────────────

function buildMarkdownArtifact(url: string, data: UrlInspectResult): string {
  const lines: string[] = [
    `# Traceability Report`,
    `**URL:** ${url}`,
    `**Match:** ${data.match_strategy} (${Math.round((data.confidence ?? STRATEGY_CONFIDENCE[data.match_strategy as string] ?? 0) * 100)}% confidence)`,
    `**Overall:** ${data.overall_status}`,
    `**Queried:** ${data.diagnostics?.queried_at ?? new Date().toISOString()}`,
    '',
    '## Signal Summary',
  ];
  for (const s of data.signals) {
    lines.push(`- **#${s.stage} ${s.label}**: ${s.status.toUpperCase()} — ${s.summary}`);
  }
  if (data.product_card?.cross_site_occurrences?.length) {
    lines.push('', '## Cross-site occurrences');
    for (const occ of data.product_card.cross_site_occurrences) {
      lines.push(`- ${occ.site_name}: ${occ.url}`);
    }
  }
  return lines.join('\n');
}

function CopyArtifact({ url, data }: { url: string; data: UrlInspectResult }) {
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedMd, setCopiedMd] = useState(false);

  const copyJson = useCallback(async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 1800);
  }, [data]);

  const copyMd = useCallback(async () => {
    await navigator.clipboard.writeText(buildMarkdownArtifact(url, data));
    setCopiedMd(true);
    setTimeout(() => setCopiedMd(false), 1800);
  }, [url, data]);

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={copyJson}>
        {copiedJson ? <Check size={12} /> : <Copy size={12} />}
        {copiedJson ? 'Copied!' : 'Copy JSON'}
      </Button>
      <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={copyMd}>
        {copiedMd ? <Check size={12} /> : <Copy size={12} />}
        {copiedMd ? 'Copied!' : 'Copy as Markdown'}
      </Button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Traceability() {
  const { selectedSite } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputVal, setInputVal] = useState(searchParams.get('q') ?? '');

  // Keep the input synced with the URL's q param. Cross-site drill-down navigates to a new
  // ?q=...&site_id=... WITHOUT remounting this component, so without this sync the field would
  // retain the OLD url and Diagnose would submit a stale q paired with the new site_id.
  const qParam = searchParams.get('q') ?? '';
  useEffect(() => { setInputVal(qParam); }, [qParam]);

  // site_id in query params takes precedence — cross-site drill-down passes the target
  // site explicitly so the backend resolver scopes to the correct site, not the active one.
  const siteId   = searchParams.get('site_id') ?? selectedSite?.id ?? selectedSite?._id;
  const queryUrl = searchParams.get('q') ?? undefined;

  const { data, isLoading, error } = useUrlInspect(queryUrl, siteId);

  const handleDiagnose = useCallback(() => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    const site_id = searchParams.get('site_id');
    setSearchParams({ q: trimmed, ...(site_id ? { site_id } : {}) }, { replace: false });
  }, [inputVal, searchParams, setSearchParams]);

  const crossSiteOccurrences = data?.product_card?.cross_site_occurrences ?? [];

  return (
    <div className="p-6 max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Product Traceability</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Paste a product URL to see its full pipeline status across all pipeline signals.
        </p>
      </div>

      {/* URL input */}
      <div className="flex gap-2 max-w-2xl">
        <Input
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleDiagnose(); }}
          placeholder="https://your-site.com/products/example-product"
          className="h-10 text-sm font-mono"
        />
        <Button onClick={handleDiagnose} className="h-10 px-5" style={{ backgroundColor: catalogColors.accentTeal }}>
          <Search className="h-4 w-4 mr-1.5" />
          Diagnose
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive p-3 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {(error as Error).message}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-8 rounded" />
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
      )}

      {/* Results */}
      {data && queryUrl && (
        <div className="space-y-4">
          {/* Match strategy + overall health row */}
          <MatchStrategyBanner data={data} />
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <HealthBanner status={data.overall_status} />
            <CopyArtifact url={queryUrl} data={data} />
          </div>

          {/* Product identity (product_card) */}
          {data.product_card && (
            <div className="rounded-lg border p-4 space-y-1">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-semibold text-base">{data.product_card.product_name}</div>
                  {data.product_card.brand && (
                    <div className="text-xs text-muted-foreground">{data.product_card.brand}</div>
                  )}
                </div>
                {data.product_card.product_url && (
                  <a
                    href={data.product_card.product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs flex items-center gap-1 hover:underline"
                    style={{ color: catalogColors.accentTeal }}
                  >
                    <ExternalLink size={11} /> View on site
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-xs text-muted-foreground">
                {data.product_card.product_id && <span>ID: <span className="font-mono">{data.product_card.product_id}</span></span>}
                {data.product_card.gtin       && <span>GTIN: {data.product_card.gtin}</span>}
                {data.product_card.category   && <span>{data.product_card.category}</span>}
              </div>
            </div>
          )}

          {/* 12-signal accordion */}
          <Accordion type="multiple" className="space-y-2">
            {data.signals.map((signal) => (
              <SignalCard key={signal.stage} signal={signal} />
            ))}
          </Accordion>

          {/* Cross-site drill-down */}
          {crossSiteOccurrences.length > 0 && (
            <CrossSitePanel occurrences={crossSiteOccurrences} />
          )}
        </div>
      )}

      {/* Empty state */}
      {!queryUrl && !isLoading && (
        <div className="text-center py-24 text-muted-foreground text-sm">
          Enter a product URL above and click <strong>Diagnose</strong> to inspect its pipeline state.
        </div>
      )}
    </div>
  );
}
